import {makeAutoObservable, observable} from 'mobx'
import {ChatMessage, Hub} from 'yougile-model'
import {PermissionsChecker} from '@client/permissions-checker'
import {Tracking} from '@common/tracking'
import {TrackingEvent} from '@common/tracking-events'
import {createUseStore} from '@containers/hooks'
import {ChatUtils} from '@extensions/chat-utils'
import {ChatStore} from '../chat-store'

interface State {
  edited: ChatMessage | undefined
  newText: string
  labels: Array<{label: string; timestamp: number}>
  allowEditPins: boolean
  hub?: Hub
}

export class ChatLabelsStore {
  state: State = {
    edited: undefined,
    newText: '',
    labels: [],
    allowEditPins: false
  }

  constructor(private chatStore: ChatStore) {
    makeAutoObservable(this, {
      state: observable.shallow
    })

    this.chatStore = chatStore
  }

  syncOnUpdate(): void {
    this.state.hub = this.chatStore.state.hub
    this.state.allowEditPins =
      !!this.state.hub &&
      (this.chatStore.state.noTabs ||
        PermissionsChecker.checkEditPins(this.state.hub))
    this.updateLabels()
  }

  updateLabels(): void {
    if (!this.state.hub) {
      return
    }
    this.state.labels = this.state.hub.chat.getLabels()
  }

  goTo(label: string): void {
    void Tracking.send(TrackingEvent.chatFollowQuicklink)
    ChatUtils.goToId(
      this.chatStore.messagesContainerElement,
      ChatUtils.toChatLabelId(label)
    )
  }

  keyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Enter') {
      event.preventDefault()
      this.stopEdit()
    } else if (event.key === 'Escape') {
      this.state.edited = undefined
      this.state.newText = ''
    }
  }

  // todo: also used in chat-message-base.tsx, move to common utils
  escape(id: string): string {
    return 'chat-label-' + window.btoa(encodeURIComponent(id)).replace(/=/g, '')
  }

  startEdit(msg: ChatMessage): void {
    if (!this.state.allowEditPins) {
      return
    }
    this.state.edited = msg
    this.state.newText = msg.label
  }

  removeLabel(msg: ChatMessage): void {
    this.state.edited = msg
    this.stopEditNew('')
  }

  stopEdit(): void {
    const msg = this.state.edited
    const changed =
      msg && this.state.hub?.chat.addLabel(msg.timestamp, this.state.newText)
    if (changed) {
      void Tracking.send(TrackingEvent.chatEditQuicklink)
      this.updateLabels()
      this.state.hub?.commit()
    }
    this.state.edited = undefined
    this.state.newText = ''
  }

  stopEditNew(label?: string): void {
    if (typeof label !== 'undefined') {
      const msg = this.state.edited
      const changed = msg && this.state.hub?.chat.addLabel(msg.timestamp, label)
      if (changed) {
        void Tracking.send(TrackingEvent.chatEditQuicklink)
        this.updateLabels()
        this.state.hub?.commit()
      }
    }
    this.state.edited = undefined
  }

  onTextChange(e: React.ChangeEvent<HTMLInputElement>): void {
    this.state.newText = e.target.value
  }
}

export const [ChatLabelsContext, useChatLabelsStore] =
  createUseStore(ChatLabelsStore)
